import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, updateProfile, sendPasswordResetEmail, User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { auth, db, handleFirestoreError, OperationType } from '../firebase';
import { getLocalizedValue } from '../lib/utils/localization';

export type UserRole = 'buyer' | 'professional' | 'admin';

export interface User {
  uid: string;
  email: string | null;
  role: UserRole;
  name: string;
  professionalId?: string;
  stripeConnectStatus?: 'pending' | 'active';
  stripe_customer_id?: string;
  plan?: 'basic' | 'business' | 'executive';
  subscription_status?: string;
  trial_end?: number;
  current_period_end?: number;
  cancel_at_period_end?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, planType?: string) => Promise<string>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Safety timeout to prevent infinite loading
    const safetyTimeout = setTimeout(() => {
      if (loading) {
        console.warn('AGP: Auth loading safety timeout reached. Forcing loading to false.');
        setLoading(false);
      }
    }, 8000); // 8 seconds

    if (!auth || !db) {
      setLoading(false);
      clearTimeout(safetyTimeout);
      return;
    }

    let unsubscribeSnapshot: (() => void) | null = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      clearTimeout(safetyTimeout);
      try {
        if (unsubscribeSnapshot) {
          unsubscribeSnapshot();
          unsubscribeSnapshot = null;
        }

        if (firebaseUser) {
          const isAdminEmail = ['marketemprende@gmail.com'].includes(firebaseUser.email?.trim().toLowerCase() || '');
          
          // First check which collection the user is in
          let collectionName = 'vendedores';
          let userDocRef = doc(db, collectionName, firebaseUser.uid);
          let userDoc = await getDoc(userDocRef);
          
          if (!userDoc.exists()) {
            collectionName = 'users';
            userDocRef = doc(db, collectionName, firebaseUser.uid);
            userDoc = await getDoc(userDocRef);
          }

          if (userDoc.exists()) {
            // Set initial state synchronously from the getDoc result
            const userData = userDoc.data();
            const role = isAdminEmail ? 'admin' : (userData.role || (collectionName === 'vendedores' ? 'professional' : 'buyer'));
            
            // Ensure name is a string
            let name = userData.companyName || userData.displayName || 'User';
            if (typeof name === 'object') {
              name = getLocalizedValue(name, 'es'); // Default to Spanish if we don't have i18n here
            }

            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              name: name,
              role: role,
              plan: userData.plan,
              stripeConnectStatus: userData.stripeConnectStatus,
              stripe_customer_id: userData.stripe_customer_id,
              subscription_status: userData.subscription_status,
              trial_end: userData.trial_end,
              current_period_end: userData.current_period_end,
              cancel_at_period_end: userData.cancel_at_period_end,
            });

            // Set up real-time listener for future updates
            unsubscribeSnapshot = onSnapshot(userDocRef, (docSnapshot) => {
              if (docSnapshot.exists()) {
                const updatedData = docSnapshot.data();
                const updatedRole = isAdminEmail ? 'admin' : (updatedData.role || (collectionName === 'vendedores' ? 'professional' : 'buyer'));
                
                let updatedName = updatedData.companyName || updatedData.displayName || 'User';
                if (typeof updatedName === 'object') {
                  updatedName = getLocalizedValue(updatedName, 'es');
                }

                setUser({
                  uid: firebaseUser.uid,
                  email: firebaseUser.email,
                  name: updatedName,
                  role: updatedRole,
                  plan: updatedData.plan,
                  stripeConnectStatus: updatedData.stripeConnectStatus,
                  stripe_customer_id: updatedData.stripe_customer_id,
                  subscription_status: updatedData.subscription_status,
                  trial_end: updatedData.trial_end,
                  current_period_end: updatedData.current_period_end,
                  cancel_at_period_end: updatedData.cancel_at_period_end,
                });
              }
            }, (error) => {
              console.error('Error listening to user profile:', error);
            });
          } else {
            // Fallback for new users or incomplete profiles, or admin if not in DB yet
            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              name: firebaseUser.displayName || 'User',
              role: isAdminEmail ? 'admin' : 'buyer',
            });
          }
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // Fallback to basic user info if profile fetch fails
        if (firebaseUser) {
          const isAdminEmail = ['marketemprende@gmail.com'].includes(firebaseUser.email?.trim().toLowerCase() || '');
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            name: firebaseUser.displayName || 'User',
            role: isAdminEmail ? 'admin' : 'buyer',
          });
        }
      } finally {
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
      }
    };
  }, []);

  const login = async (email: string, password: string) => {
    if (!auth) throw new Error('Firebase Auth is not initialized');
    
    const testEmails = [
      'pedidos@greeninstallers.example.com',
      'orders@prograss.example.com',
      'ana.garcia@example.com',
      'pedro.sanchez@example.com',
      'club.tenis@example.com',
      'marta.lopez@example.com',
      'contact@grassmaster.example.com',
      'info@greeninstallers.example.com',
      'sales@ecoturf.example.com',
      'hello@prograss.example.com',
      'hello@turfexperts.co.uk',
      'contact@gazon-fr.com',
      'commandes@gazon-fr.com'
    ];

    const isTestEmail = testEmails.includes(email);
    const fixedPassword = 'password123'; // Fixed password for all test users to allow "any password" claim

    try {
      // For test users, we try with the fixed password first if they typed something else
      // but to be safe we try with what they typed first
      await signInWithEmailAndPassword(auth, email, isTestEmail ? fixedPassword : password);
    } catch (error: any) {
      // If it's a test email and login fails, try to register it
      if (isTestEmail && (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found')) {
        try {
          const isProfessional = email.includes('example.com') || email.includes('.co.uk') || email.includes('.com');
          // More specific check for professionals vs buyers based on the list
          const professionalEmails = [
            'contact@grassmaster.example.com',
            'info@greeninstallers.example.com',
            'sales@ecoturf.example.com',
            'hello@prograss.example.com',
            'hello@turfexperts.co.uk',
            'contact@gazon-fr.com',
            'commandes@gazon-fr.com'
          ];
          const isAdminEmail = email.trim().toLowerCase() === 'marketemprende@gmail.com';
          
          let role: UserRole = 'buyer';
          if (isAdminEmail) role = 'admin';
          else if (professionalEmails.includes(email)) role = 'professional';

          await register(
            email.split('@')[0], 
            email, 
            fixedPassword, 
            role === 'professional' ? 'business' : undefined
          );

          // If it was admin, we need to manually set the role in Firestore because register doesn't handle 'admin' role directly
          if (isAdminEmail) {
            const userCredential = await signInWithEmailAndPassword(auth, email, fixedPassword);
            await setDoc(doc(db, 'users', userCredential.user.uid), {
              uid: userCredential.user.uid,
              email: email,
              role: 'admin',
              displayName: 'AGP Admin',
              createdAt: new Date().toISOString()
            });
          }
          return;
        } catch (regError) {
          console.error('Test user auto-registration failed:', regError);
          throw error; // Throw original login error
        }
      }
      throw error;
    }
  };

  const register = async (name: string, email: string, password: string, planType?: string): Promise<string> => {
    if (!auth || !db) throw new Error('Firebase is not initialized');
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    try {
      await updateProfile(firebaseUser, { displayName: name });
    } catch (err) {
      console.error('Error updating profile:', err);
    }

    const isProfessional = !!planType;
    const collectionName = isProfessional ? 'vendedores' : 'users';
    
    const profileData: any = {
      uid: firebaseUser.uid,
      email: email,
      createdAt: new Date().toISOString(),
    };

    if (isProfessional) {
      profileData.companyName = name;
      profileData.plan = planType;
      profileData.role = 'professional';
      profileData.status = 'pending';
    } else {
      profileData.displayName = name;
      profileData.role = 'buyer';
    }

    try {
      await setDoc(doc(db, collectionName, firebaseUser.uid), profileData);
      
      // Update local state immediately so the UI reflects the new name
      setUser(prev => prev ? { ...prev, name: name } : {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        name: name,
        role: isProfessional ? 'professional' : 'buyer',
        plan: planType as any
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, `${collectionName}/${firebaseUser.uid}`);
    }

    return firebaseUser.uid;
  };

  const logout = async () => {
    if (!auth) throw new Error('Firebase Auth is not initialized');
    await signOut(auth);
  };

  const resetPassword = async (email: string) => {
    try {
      const response = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send password reset email');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, resetPassword }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
